import { request } from '@koi-design/vix-tools';
import { message } from '@koi-design/uxd-ui';
import store from '@/store';

// The project-level packApi maps envelope code 400001 to session expiry,
// but the universal-agent backend uses 400001 for field validation failures.
// Mutations therefore go through a local wrapper that only surfaces the
// backend message and resolves a stable result object.
const universalMutationApi = requestParams =>
  new Promise(resolve => {
    request({ ...requestParams, catchAll: true })
      .then(res => {
        if (res.data.code === '000000') {
          resolve({ ok: true, data: res.data.data ?? null, message: res.data.message });
          return;
        }
        message.error(res.data.message);
        resolve({ ok: false, data: null, message: res.data.message });
      })
      .catch(() => {
        // Network layer failures are already toasted by the global handlers.
        resolve({ ok: false, data: null, message: '' });
      });
  });

// ---------- Status ----------

// Availability flag of the super entry surface. Resolves to the response data
// (`{ available: boolean }`); `available: false` means the conversation /
// scheduled tasks / agent config entries stay hidden and the menu opens the
// Vibe Agent page instead.
export const getUniversalStatusApi = () =>
  request({ url: '/api/v1/universal/status', method: 'get' });

// ---------- Session ----------

// POST without a body by default. The Vibe Agent entry is the only caller
// that passes `{ source: 'vibe-agent' }`; regular sessions stay body-less.
export const generateUniversalSessionApi = data =>
  request({
    url: '/api/v1/universal/session/generate',
    method: 'post',
    ...(data === undefined ? {} : { data })
  });

export const getUniversalSessionListApi = data =>
  request({ url: '/api/v1/universal/session/list', method: 'get', data });

export const getUniversalArchivedSessionListApi = data =>
  request({ url: '/api/v1/universal/session/archived', method: 'get', data });

export const getUniversalSessionInfoApi = sessionId =>
  request({ url: `/api/v1/universal/session/${sessionId}/info`, method: 'get' });

export const archiveUniversalSessionApi = sessionId =>
  universalMutationApi({
    url: `/api/v1/universal/session/${sessionId}/archive`,
    method: 'post'
  });

export const unarchiveUniversalSessionApi = sessionId =>
  universalMutationApi({
    url: `/api/v1/universal/session/${sessionId}/unarchive`,
    method: 'post'
  });

export const pinUniversalSessionApi = sessionId =>
  universalMutationApi({
    url: `/api/v1/universal/session/${sessionId}/pin`,
    method: 'post'
  });

export const unpinUniversalSessionApi = sessionId =>
  universalMutationApi({
    url: `/api/v1/universal/session/${sessionId}/unpin`,
    method: 'post'
  });

export const updateUniversalSessionTitleApi = (sessionId, title) =>
  universalMutationApi({
    url: `/api/v1/universal/session/${sessionId}/title`,
    method: 'put',
    data: { title }
  });

// Soft delete: the session disappears from list/archived/info with no undo.
export const deleteUniversalSessionApi = sessionId =>
  universalMutationApi({
    url: `/api/v1/universal/session/${sessionId}/delete`,
    method: 'post'
  });

// Bind uploaded files to a universal session. Idempotent on file_uuid;
// repeated submissions do not duplicate the binding.
export const bindUniversalSessionFilesApi = (sessionId, files) =>
  universalMutationApi({
    url: `/api/v1/universal/session/${sessionId}/files`,
    method: 'post',
    data: { files }
  });

// Unbind (remove) files from a universal session by their file_uuid list.
// Idempotent: removing an unbound file is a no-op.
export const unbindUniversalSessionFilesApi = (sessionId, fileUuids) =>
  universalMutationApi({
    url: `/api/v1/universal/session/${sessionId}/files`,
    method: 'delete',
    data: { files: fileUuids }
  });

// ---------- Session share ----------
// See docs/buddy-share/conversation-share-frontend-api.md.

// Issue a share key for one session (§2). The response data carries every
// value the share link needs (share_key / segment_code / user_id / agent_id /
// tenant_id); each call mints a fresh key and old keys stay valid.
export const createUniversalSessionShareKeyApi = segmentCode =>
  universalMutationApi({
    url: '/api/v1/universal/session/share/key',
    method: 'post',
    data: { segment_code: segmentCode }
  });

// Anonymous share-page read of one session's STM records (§3). The endpoint
// passthroughs the memory service, so success is the numeric code 0 while
// errors come back as non-200 statuses; validateStatus keeps every response
// on the resolve path so the share page can branch on the HTTP status
// (401 invalid link / 404 content gone) without triggering global toasts.
export const getUniversalSharedStmListApi = ({
  shareKey,
  segmentCode,
  userId,
  agentId,
  tenantId,
  page = 1,
  pageSize = 20
}) =>
  new Promise(resolve => {
    request({
      url: '/openapi/v1/universal/stm/list',
      method: 'post',
      catchAll: true,
      validateStatus: () => true,
      data: {
        share_key: shareKey,
        segment_code: segmentCode,
        user_id: userId,
        agent_id: agentId,
        tenant_id: tenantId,
        page,
        page_size: pageSize
      }
    })
      .then(response => {
        const status = response?.status || 0;
        const body = response?.data;
        if (status === 200 && body && Number(body.code) === 0) {
          resolve({
            ok: true,
            data: body.data ?? null,
            message: body.message || '',
            status
          });
          return;
        }
        resolve({
          ok: false,
          data: null,
          message: body?.message || '',
          status
        });
      })
      .catch(() => {
        // Network-layer failure: no HTTP status, treat as retryable.
        resolve({ ok: false, data: null, message: '', status: 0 });
      });
  });

// User-level file library (cross-session): normal files paginated in `items`,
// memory/persona files returned in full via `memory_items` (docs §3.10).
export const getUniversalUserFilesApi = data =>
  request({ url: '/api/v1/universal/user/files', method: 'get', data });

// ---------- Scheduled task ----------

export const createUniversalScheduledTaskApi = data =>
  universalMutationApi({
    url: '/api/v1/universal/scheduled-task/create',
    method: 'post',
    data
  });

export const getUniversalScheduledTaskListApi = data =>
  request({ url: '/api/v1/universal/scheduled-task/list', method: 'get', data });

export const getUniversalScheduledTaskDetailApi = taskId =>
  request({ url: `/api/v1/universal/scheduled-task/${taskId}`, method: 'get' });

export const updateUniversalScheduledTaskApi = (taskId, data) =>
  universalMutationApi({
    url: `/api/v1/universal/scheduled-task/${taskId}`,
    method: 'put',
    data
  });

export const pauseUniversalScheduledTaskApi = taskId =>
  universalMutationApi({
    url: `/api/v1/universal/scheduled-task/${taskId}/pause`,
    method: 'post'
  });

export const resumeUniversalScheduledTaskApi = taskId =>
  universalMutationApi({
    url: `/api/v1/universal/scheduled-task/${taskId}/resume`,
    method: 'post'
  });

export const executeUniversalScheduledTaskApi = taskId =>
  universalMutationApi({
    url: `/api/v1/universal/scheduled-task/${taskId}/execute`,
    method: 'post'
  });

export const getUniversalScheduledTaskRecordsApi = (agentId, data) =>
  request({ url: `/api/v1/universal/scheduled-task/${agentId}/records`, method: 'get', data });

// Note: the channel gateway no longer exposes abort/end endpoints. Abort is
// done by closing the SSE connection (the gateway forwards the abort signal
// to the backend); conversations are cleaned up by the gateway idle timeout.

// ---------- Universal agent config ----------
// Note: this domain uses the /api/universal_agent/* prefix (agent-server-g),
// distinct from the session domain /api/v1/universal/*.

// Current identity for the agent config domain: user_id (homelandInfo.user)
// and homeland_id (homelandInfo.homeland), resolved at call time.
const getUniversalAgentIdentity = () => {
  const homelandInfo = store.getters?.homelandInfo || {};
  return {
    userId: homelandInfo.user || '',
    homelandId: homelandInfo.homeland || ''
  };
};

// GET with get-or-create semantics. user_id comes from the JWT on the backend,
// so only homeland_id is sent as a query param.
export const getUniversalAgentApi = () => {
  const { homelandId } = getUniversalAgentIdentity();
  return request({
    url: '/api/universal_agent/agent',
    method: 'get',
    data: { homeland_id: homelandId }
  });
};

// Resource summary per source scope (docs §1.1.1): available resource counts
// for space / enterprise / platform, plus a cross-scope total. The total is the
// sum of the three groups and may count a resource present in several scopes
// more than once, so it never represents a distinct resource count.
export const getUniversalAgentSummaryApi = () => {
  const { homelandId } = getUniversalAgentIdentity();
  return request({
    url: '/api/universal_agent/agent/summary',
    method: 'get',
    data: { homeland_id: homelandId }
  });
};

// Partial update per the SE-A1 contract: all fields are optional and only the
// submitted ones are applied (name, avatar, avatar_color, description,
// base_setting, reply_language, reply_style, chat_model, temperature, top_p,
// max_tokens, max_context_tokens, long_term_memory_enable). user_id comes from
// the JWT; homeland_id is carried in the body. removeEmptyFields is disabled so
// clearing an optional string field (e.g. description) still submits it.
export const updateUniversalAgentApi = data => {
  const { homelandId } = getUniversalAgentIdentity();
  return universalMutationApi({
    url: '/api/universal_agent/agent',
    method: 'put',
    data: { homeland_id: homelandId, ...data },
    removeEmptyFields: false
  });
};

// Incremental relation update: each item carries an explicit action
// (bind / unbind) and only changed entries are submitted. user_id comes from
// the JWT; homeland_id is carried in the body.
// removeEmptyFields is disabled so an empty change set is still sent.
export const updateUniversalAgentRelationsApi = relations => {
  const { homelandId } = getUniversalAgentIdentity();
  return universalMutationApi({
    url: '/api/universal_agent/agent/relations',
    method: 'put',
    data: { homeland_id: homelandId, relations },
    removeEmptyFields: false
  });
};

// Resource catalog query params per the contract: scope defaults to all;
// user_id is recommended so the response carries bound; homeland_id is
// required for non-platform scopes. is_bound is optional (1 bound / 0 unbound).
// keyword is optional and filters by name on the backend.
const buildCatalogQuery = ({ scope = 'all', page = 1, pageSize = 100, is_bound, keyword } = {}) => {
  const { userId, homelandId } = getUniversalAgentIdentity();
  return {
    scope,
    user_id: userId,
    homeland_id: homelandId,
    page,
    page_size: pageSize,
    ...(is_bound === undefined ? {} : { is_bound }),
    ...(keyword ? { keyword } : {})
  };
};

export const getUniversalSkillResourcesApi = (query = {}) =>
  request({
    url: '/api/universal_agent/resources/skills',
    method: 'get',
    data: buildCatalogQuery(query)
  });

export const getUniversalPluginResourcesApi = (query = {}) =>
  request({
    url: '/api/universal_agent/resources/plugins',
    method: 'get',
    data: buildCatalogQuery(query)
  });

// Composer scoped chat model options, grouped by model classification.
// homeland_id is an optional query param that the backend currently only
// format-checks; it is reserved for future permission scoping. An unconfigured
// backend returns an empty array instead of an error.
export const getUniversalChatModelOptionsApi = () => {
  const { homelandId } = getUniversalAgentIdentity();
  return request({
    url: '/api/universal_agent/chat_model_options',
    method: 'get',
    data: { homeland_id: homelandId }
  });
};

// ---------- Memory (STM conversations / LTM facts) ----------
// The memory read side resolves success with a numeric code (0) instead of the
// management-plane "000000", so the envelope is unwrapped locally via
// catchAll. user_id resolves from the store identity like the agent-config
// domain; tenant_id is fixed per the memory contract.
const universalMemoryApi = requestParams =>
  new Promise(resolve => {
    request({
      ...requestParams,
      catchAll: true,
      headers: { 'X-Tenant-ID': 'universal_agent', ...(requestParams.headers || {}) }
    })
      .then(res => {
        const payload = res?.data;
        if (payload && Number(payload.code) === 0) {
          resolve({ ok: true, data: payload.data ?? null, message: payload.message || '' });
          return;
        }
        resolve({ ok: false, data: null, message: payload?.message || '' });
      })
      .catch(() => {
        // Network layer failures are already toasted by the global handlers.
        resolve({ ok: false, data: null, message: '' });
      });
  });

export const getUniversalStmConversationListApi = ({
  segmentCode,
  agentId,
  page = 1,
  pageSize = 100
}) => {
  const { userId } = getUniversalAgentIdentity();
  return universalMemoryApi({
    url: '/api/v1/universal/stm/list',
    method: 'post',
    data: {
      segment_code: segmentCode,
      user_id: userId,
      agent_id: agentId,
      tenant_id: 'universal_agent',
      page,
      page_size: pageSize
    }
  });
};

// Non-paginated: returns every active memory segment of the user+agent,
// newest first. The list feeds the segment picker before querying STM
// conversations.
export const getUniversalStmSegmentListApi = ({ agentId }) => {
  const { userId } = getUniversalAgentIdentity();
  return universalMemoryApi({
    url: '/api/v1/universal/stm/segment/list',
    method: 'post',
    data: {
      user_id: userId,
      agent_id: agentId,
      tenant_id: 'universal_agent'
    }
  });
};

// LTM facts pagination: newest facts first, page-based like the STM list.
export const getUniversalLtmFactsListApi = ({ agentId, page = 1, pageSize = 20 }) => {
  const { userId } = getUniversalAgentIdentity();
  return universalMemoryApi({
    url: '/api/v1/universal/ltm/facts/list',
    method: 'post',
    data: {
      user_id: userId,
      agent_id: agentId,
      tenant_id: 'universal_agent',
      page,
      page_size: pageSize
    }
  });
};

// Deletes one stored STM conversation record by its numeric id. The tenant is
// carried by the X-Tenant-ID header.
export const deleteUniversalStmConversationApi = id =>
  universalMemoryApi({
    url: `/api/v1/universal/stm/conversation/${id}`,
    method: 'delete'
  });

// Deletes one LTM fact. fact_id is numeric in the request but a string in the
// response; username must match the write-time value (the JWT login name the
// gateway injects, mirrored by the stored user info).
export const deleteUniversalLtmFactApi = ({ agentId, factId, username }) => {
  const { userId } = getUniversalAgentIdentity();
  return universalMemoryApi({
    url: '/api/v1/universal/ltm/facts/delete',
    method: 'post',
    data: {
      user_id: userId,
      agent_id: agentId,
      tenant_id: 'universal_agent',
      username,
      fact_id: factId
    }
  });
};
